import {
    Add as AddIcon,
    Cancel as CancelIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import {
    Alert,
    alpha,
    Box,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
    useTheme
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ApplicationButton, LocalizedTextField } from '../../../../MicrospecDynamicUI/Components';
import { useOrganizationContext } from '../../../../MicrospecDynamicUI/Dashboard/OrganizationContext';
import { useMicrospecDynamicUIContext } from '../../../../MicrospecDynamicUI/GlobalProvider';
import { getDataFromCollectionWithQuery, getDocById, saveDocument } from '../../../../MicrospecDynamicUI/Helpers/dataUtils';
import { BusinessSpecification } from '../../../../MicrospecDynamicUI/Models/Business/BusinessSpecification';
import { BusinessSpecificationCategory } from '../../../../MicrospecDynamicUI/Models/Business/BusinessSpecificationCategory';
import { useMicrospecThemeLanguageContext } from '../../../../MicrospecDynamicUI/Themes';
import { SPECIFICATION_CATEGORIES_COLLECTION, SPECIFICATIONS_COLLECTION } from '../../../constants/collectionPaths';

type SpecType = 'BOOLEAN' | 'NUMBER_RANGE' | 'LIST_OF_VALUES' | 'COLOR_PICKER';

interface UpsertSpecificationProps {
  specificationId?: string;
  onSave?: (item: BusinessSpecification) => void;
  onCancel?: () => void;
}

const defaultTypeCharacteristics: Record<SpecType, Record<string, any>> = {
  BOOLEAN: {},
  NUMBER_RANGE: { min: 0, max: 100, independentOnOrderQuantity: false },
  LIST_OF_VALUES: { values: [] },
  COLOR_PICKER: { colors: [] },
};

const UpsertSpecification: React.FC<UpsertSpecificationProps> = ({ specificationId, onSave, onCancel }) => {
  const { localizationProvider } = useMicrospecThemeLanguageContext();
  const { translate } = localizationProvider;
  const theme = useTheme();
  const { usedLocalization } = useOrganizationContext();
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { firestore } = cloudProvider;
  const db = firestore?.instance;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [specCategories, setSpecCategories] = useState<BusinessSpecificationCategory[]>([]);
  const [listInput, setListInput] = useState('');
  const [colorInput, setColorInput] = useState('#000000');
  const [priceMode, setPriceMode] = useState<'flat' | 'per_option'>('flat');

  const [formData, setFormData] = useState<BusinessSpecification>({
    specification_category_id: '',
    name: usedLocalization?.supported?.length > 1 ? {} : '',
    type: 'BOOLEAN',
    type_characteristics: {},
    additional_price: 0,
    is_available: true,
  });

  // Load specification categories
  useEffect(() => {
    const fetch = async () => {
      if (!db) return;
      try {
        const data = await getDataFromCollectionWithQuery(null, null, db, SPECIFICATION_CATEGORIES_COLLECTION, [], true, 200);
        setSpecCategories(data as BusinessSpecificationCategory[]);
      } catch (err) {
        console.error('Error fetching spec categories:', err);
      }
    };
    fetch();
  }, [db]);

  // Load existing specification if editing
  useEffect(() => {
    const load = async () => {
      if (!specificationId || !db) return;
      setLoading(true);
      try {
        const data = await getDocById(null, null, db, SPECIFICATIONS_COLLECTION, specificationId, false);
        if (data) {
          setFormData({
            specification_category_id: data.specification_category_id || '',
            name: data.name || '',
            type: data.type || 'BOOLEAN',
            type_characteristics: data.type_characteristics || {},
            additional_price: data.additional_price ?? 0,
            is_available: data.is_available ?? true,
          });
          setPriceMode(typeof data.additional_price === 'object' && data.additional_price !== null ? 'per_option' : 'flat');
        }
      } catch (err) {
        setError(translate('specification.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [specificationId, db, translate]);

  const handleInputChange = (field: keyof BusinessSpecification, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleTypeChange = (type: SpecType) => {
    setPriceMode('flat');
    setFormData(prev => ({
      ...prev,
      type,
      type_characteristics: { ...defaultTypeCharacteristics[type] },
      additional_price: 0,
    }));
  };

  const handleCharChange = (key: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      type_characteristics: { ...prev.type_characteristics, [key]: value },
    }));
  };

  const addListValue = () => {
    if (!listInput.trim()) return;
    const current = (formData.type_characteristics?.values as string[]) || [];
    handleCharChange('values', [...current, listInput.trim()]);
    if (priceMode === 'per_option') {
      const priceMap = typeof formData.additional_price === 'object' ? { ...formData.additional_price } : {};
      priceMap[String(current.length)] = 0;
      handleInputChange('additional_price', priceMap);
    }
    setListInput('');
  };

  const removeListValue = (index: number) => {
    const current = (formData.type_characteristics?.values as string[]) || [];
    handleCharChange('values', current.filter((_, i) => i !== index));
    if (priceMode === 'per_option') {
      const oldMap = typeof formData.additional_price === 'object' ? { ...formData.additional_price } : {};
      const newMap: Record<string, number> = {};
      let newIdx = 0;
      for (let i = 0; i < current.length; i++) {
        if (i !== index) { newMap[String(newIdx)] = oldMap[String(i)] ?? 0; newIdx++; }
      }
      handleInputChange('additional_price', newMap);
    }
  };

  const addColor = () => {
    const current = (formData.type_characteristics?.colors as string[]) || [];
    if (!current.includes(colorInput)) {
      handleCharChange('colors', [...current, colorInput]);
      if (priceMode === 'per_option') {
        const priceMap = typeof formData.additional_price === 'object' ? { ...formData.additional_price } : {};
        priceMap[colorInput] = 0;
        handleInputChange('additional_price', priceMap);
      }
    }
  };

  const removeColor = (index: number) => {
    const current = (formData.type_characteristics?.colors as string[]) || [];
    const removedColor = current[index];
    handleCharChange('colors', current.filter((_, i) => i !== index));
    if (priceMode === 'per_option') {
      const oldMap = typeof formData.additional_price === 'object' ? { ...formData.additional_price } : {};
      const newMap = { ...oldMap };
      delete newMap[removedColor];
      handleInputChange('additional_price', newMap);
    }
  };

  const validateForm = (): boolean => {
    if (!formData.specification_category_id) { setError(translate('specification.categoryRequired')); return false; }
    const name = formData.name;
    if (typeof name === 'string' && !name.trim()) { setError(translate('specification.nameRequired')); return false; }
    if (typeof name === 'object' && !Object.values(name).some(v => v.trim())) { setError(translate('specification.nameRequired')); return false; }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm() || !db) return;
    setSaving(true);
    setError(null);
    try {
      const result = await saveDocument(firestore, SPECIFICATIONS_COLLECTION, formData, { id: specificationId });
      setSuccess(true);
      if (onSave) {
        onSave({ ...formData, id: result.id });
      } else {
        if (!specificationId) setFormData({ specification_category_id: '', name: '', type: 'BOOLEAN', type_characteristics: {}, additional_price: 0, is_available: true });
        setSuccess(false);
      }
    } catch (err: any) {
      setError(err.message || translate('specification.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    else { setFormData({ specification_category_id: '', name: '', type: 'BOOLEAN', type_characteristics: {}, additional_price: 0, is_available: true }); setError(null); }
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;

  const cardinalColor = theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[900];

  return (
    <Card sx={{ borderRadius: 3, boxShadow: theme.palette.mode === 'dark' ? `0 8px 32px ${alpha(theme.palette.common.black, 0.4)}` : `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}` }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3, color: cardinalColor }}>
          {specificationId ? translate('specification.editTitle') : translate('specification.createTitle')}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }}>{translate('specification.savedSuccessfully')}</Alert>}

        <Grid container spacing={3}>
          {/* Specification Category */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>{translate('specification.category')}</InputLabel>
              <Select value={formData.specification_category_id} onChange={(e) => handleInputChange('specification_category_id', e.target.value)} label={translate('specification.category')}>
                {specCategories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>{translate(cat.name)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Name */}
          <Grid item xs={12} md={6}>
            {usedLocalization?.supported?.length > 1 ? (
              <LocalizedTextField
                languageConfig={usedLocalization}
                value={typeof formData.name === 'object' ? formData.name : { [usedLocalization.preferred]: formData.name || '' }}
                onChange={(value: any) => handleInputChange('name', value)}
                label={translate('specification.name')}
                required
                helperText={translate('specification.enterName')}
                variant="outlined"
              />
            ) : (
              <TextField
                fullWidth
                label={translate('specification.name')}
                value={typeof formData.name === 'string' ? formData.name : (formData.name as any)?.[usedLocalization?.preferred || 'en-US'] || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                helperText={translate('specification.enterName')}
                variant="outlined"
              />
            )}
          </Grid>

          {/* Type */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>{translate('specification.type')}</InputLabel>
              <Select value={formData.type} onChange={(e) => handleTypeChange(e.target.value as SpecType)} label={translate('specification.type')}>
                <MenuItem value="BOOLEAN">{translate('specification.typeBoolean')}</MenuItem>
                <MenuItem value="NUMBER_RANGE">{translate('specification.typeNumberRange')}</MenuItem>
                <MenuItem value="LIST_OF_VALUES">{translate('specification.typeListOfValues')}</MenuItem>
                <MenuItem value="COLOR_PICKER">{translate('specification.typeColorPicker')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Additional Price */}
          <Grid item xs={12} md={6}>
            {(formData.type === 'LIST_OF_VALUES' || formData.type === 'COLOR_PICKER') && (
              <FormControlLabel
                sx={{ mb: 1, display: 'block' }}
                control={
                  <Switch
                    checked={priceMode === 'per_option'}
                    size="small"
                    onChange={(e) => {
                      const mode: 'flat' | 'per_option' = e.target.checked ? 'per_option' : 'flat';
                      setPriceMode(mode);
                      if (mode === 'flat') {
                        handleInputChange('additional_price', 0);
                      } else {
                        const newMap: Record<string, number> = {};
                        if (formData.type === 'LIST_OF_VALUES') {
                          ((formData.type_characteristics?.values as string[]) || []).forEach((_, i) => { newMap[String(i)] = 0; });
                        } else {
                          ((formData.type_characteristics?.colors as string[]) || []).forEach(c => { newMap[c] = 0; });
                        }
                        handleInputChange('additional_price', newMap);
                      }
                    }}
                  />
                }
                label={<Typography variant="body2">{translate('specification.perOptionPricing')}</Typography>}
              />
            )}
            {priceMode === 'flat' && (
              <TextField
                fullWidth
                label={translate('specification.additionalPrice')}
                type="number"
                value={typeof formData.additional_price === 'number' ? formData.additional_price : 0}
                onChange={(e) => handleInputChange('additional_price', parseFloat(e.target.value) || 0)}
                helperText={translate('specification.additionalPriceHelperText')}
                variant="outlined"
                inputProps={{ min: 0, step: 0.01 }}
              />
            )}
          </Grid>

          {/* Type Characteristics */}
          {formData.type === 'BOOLEAN' && (
            <Grid item xs={12}>
              <Alert severity="info">{translate('specification.booleanInfo')}</Alert>
            </Grid>
          )}

          {formData.type === 'NUMBER_RANGE' && (
            <>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={translate('specification.rangeMin')} type="number" value={formData.type_characteristics?.min ?? 0}
                  onChange={(e) => handleCharChange('min', parseFloat(e.target.value) || 0)} variant="outlined" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={translate('specification.rangeMax')} type="number" value={formData.type_characteristics?.max ?? 100}
                  onChange={(e) => handleCharChange('max', parseFloat(e.target.value) || 0)} variant="outlined" />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.type_characteristics?.independentOnOrderQuantity ?? false}
                      onChange={(e) => handleCharChange('independentOnOrderQuantity', e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{translate('specification.independentOnOrderQuantity')}</Typography>
                      <Typography variant="caption" color="text.secondary">{translate('specification.independentOnOrderQuantityHelper')}</Typography>
                    </Box>
                  }
                />
              </Grid>
            </>
          )}

          {formData.type === 'LIST_OF_VALUES' && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>{translate('specification.listValues')}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField size="small" value={listInput} onChange={(e) => setListInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addListValue(); } }}
                  label={translate('specification.addValue')} sx={{ flexGrow: 1 }} />
                <IconButton onClick={addListValue} color="primary" sx={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}` }}>
                  <AddIcon />
                </IconButton>
              </Box>
              {priceMode === 'per_option' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {((formData.type_characteristics?.values as any[]) || []).map((val, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={translate(val)} onDelete={() => removeListValue(i)} color="primary" variant="outlined" sx={{ minWidth: 80 }} />
                      <TextField
                        size="small"
                        type="number"
                        label={translate('specification.additionalPrice')}
                        value={(typeof formData.additional_price === 'object' ? formData.additional_price[String(i)] ?? 0 : 0)}
                        onChange={(e) => {
                          const map = typeof formData.additional_price === 'object' ? { ...formData.additional_price } : {};
                          map[String(i)] = parseFloat(e.target.value) || 0;
                          handleInputChange('additional_price', map);
                        }}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{ width: 120 }}
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {((formData.type_characteristics?.values as any[]) || []).map((val, i) => (
                    <Chip key={i} label={translate(val)} onDelete={() => removeListValue(i)} color="primary" variant="outlined" />
                  ))}
                </Box>
              )}
            </Grid>
          )}

          {formData.type === 'COLOR_PICKER' && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>{translate('specification.colorOptions')}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                <Box component="input" type="color" value={colorInput} onChange={(e: any) => setColorInput(e.target.value)}
                  style={{ width: 48, height: 40, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 2 }} />
                <TextField size="small" value={colorInput} onChange={(e) => setColorInput(e.target.value)} label={translate('specification.hexColor')} sx={{ width: 140 }} />
                <IconButton onClick={addColor} color="primary" sx={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}` }}>
                  <AddIcon />
                </IconButton>
              </Box>
              {priceMode === 'per_option' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {((formData.type_characteristics?.colors as string[]) || []).map((color, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={color}
                        onDelete={() => removeColor(i)}
                        icon={<Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: color, border: '1px solid rgba(0,0,0,0.2)' }} />}
                        variant="outlined"
                      />
                      <TextField
                        size="small"
                        type="number"
                        label={translate('specification.additionalPrice')}
                        value={(typeof formData.additional_price === 'object' ? formData.additional_price[color] ?? 0 : 0)}
                        onChange={(e) => {
                          const map = typeof formData.additional_price === 'object' ? { ...formData.additional_price } : {};
                          map[color] = parseFloat(e.target.value) || 0;
                          handleInputChange('additional_price', map);
                        }}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{ width: 120 }}
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {((formData.type_characteristics?.colors as string[]) || []).map((color, i) => (
                    <Chip
                      key={i}
                      label={color}
                      onDelete={() => removeColor(i)}
                      icon={<Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: color, border: '1px solid rgba(0,0,0,0.2)' }} />}
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
            </Grid>
          )}

          {/* Available */}
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={formData.is_available} onChange={(e) => handleInputChange('is_available', e.target.checked)} color="success" />}
              label={
                <Box>
                  <Typography variant="body1" fontWeight={600}>{translate('specification.available')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.is_available ? translate('specification.availableToSelect') : translate('specification.unavailable')}
                  </Typography>
                </Box>
              }
            />
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <ApplicationButton variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel} disabled={saving} size="large">
                {translate('common.cancel')}
              </ApplicationButton>
              <ApplicationButton
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                size="large"
                sx={{
                  minWidth: 150,
                  background: theme.palette.mode === 'dark'
                    ? `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`
                    : `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                  boxShadow: `0 3px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': { boxShadow: `0 5px 20px ${alpha(theme.palette.primary.main, 0.4)}` },
                }}
              >
                {saving ? translate('common.loading') : translate('common.save')}
              </ApplicationButton>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default UpsertSpecification;
